import contentCache from './ContentCache';
import {
  putNativeAssetInventory,
  putNativeMediaAsset,
} from './NativeLoopOfflineBridge';

export const LOOP_MEDIA_INVENTORY_MAX_AGE_MS = 24 * 60 * 60 * 1000;

const INVENTORY_PREFIX = 'trillboards:loop-media-assets:v1';
const CACHEABLE_URL_SKIP_PATTERNS = [
  'youtube.com',
  'youtu.be',
  'twitch.tv',
  'player.twitch.tv',
  'vimeo.com',
  'player.vimeo.com',
  'docs.google.com',
  'slides.google.com',
  'canva.com',
];

const CACHE_METADATA_URL_KEYS = new Set([
  'asset_url',
  'assetUrl',
  'video',
  'video_url',
  'videoUrl',
  'local_file_path',
  'localFilePath',
  'image',
  'image_url',
  'imageUrl',
  'url',
]);

export const LOOP_MEDIA_PRIORITY = {
  paidAd: 1,
  houseDefault: 2,
  supportingDefault: 3,
};

export const LOOP_MEDIA_TTL_MS_BY_PRIORITY = {
  1: 48 * 60 * 60 * 1000,
  2: 24 * 60 * 60 * 1000,
  3: 12 * 60 * 60 * 1000,
};

export const buildLoopMediaInventoryKey = (screenId) =>
  `${INVENTORY_PREFIX}:${String(screenId || 'unknown')}`;

const asArray = (value) => {
  if (Array.isArray(value)) return value;
  return value == null ? [] : [value];
};

const getStorage = () => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      return window.localStorage;
    }
  } catch (_) {}
  return null;
};

const getStreamContent = (stream) => stream?.content || stream || {};

const getStringValue = (value) => {
  if (typeof value === 'string') return value.trim();
  if (value && typeof value === 'object' && typeof value.url === 'string') {
    return value.url.trim();
  }
  return '';
};

const inferLoopMediaTypeFromUrl = (url) => {
  const clean = String(url || '').split('?')[0].toLowerCase();
  if (/\.(mp4|webm|mov|m4v)$/.test(clean)) return 'video';
  if (/\.(png|jpe?g|webp|gif|avif)$/.test(clean)) return 'image';
  return 'media';
};

const looksLikeCacheableMediaUrl = (value) => {
  const url = getStringValue(value);
  if (!url || url.startsWith('blob:') || url.startsWith('data:')) {
    return false;
  }

  try {
    const parsed = new URL(url, typeof window !== 'undefined' ? window.location.href : 'https://screen.trillboards.com');
    const protocol = parsed.protocol.toLowerCase();
    if (protocol !== 'http:' && protocol !== 'https:') {
      return false;
    }
    return !CACHEABLE_URL_SKIP_PATTERNS.some((pattern) => parsed.hostname.includes(pattern));
  } catch (_) {
    return false;
  }
};

const readMediaUrlsFromContent = (content) => {
  if (!content || typeof content !== 'object') return [];

  const urls = [
    content.video,
    content.video_url,
    content.videoUrl,
    content.local_file_path,
    content.localFilePath,
    content.media?.url,
    content.asset?.url,
    content.image_url,
    content.imageUrl,
    content.logo_url,
    content.logo_transformed_url,
  ];

  asArray(content.image).forEach((item) => urls.push(item));
  asArray(content.images).forEach((item) => urls.push(item));
  asArray(content.photos).forEach((item) => urls.push(item));

  return urls
    .map(getStringValue)
    .filter(Boolean)
    .filter(looksLikeCacheableMediaUrl);
};

const addAsset = (assetsByUrl, candidate) => {
  const url = getStringValue(candidate?.url);
  if (!looksLikeCacheableMediaUrl(url)) return;

  const existing = assetsByUrl.get(url);
  const next = {
    url,
    role: candidate.role || 'loop_media',
    source: candidate.source || 'unknown',
    priority: candidate.priority || LOOP_MEDIA_PRIORITY.supportingDefault,
    ttl_ms: LOOP_MEDIA_TTL_MS_BY_PRIORITY[candidate.priority] || LOOP_MEDIA_TTL_MS_BY_PRIORITY[3],
    order: Number.isFinite(candidate.order) ? candidate.order : 999,
    media_type: candidate.media_type || candidate.mediaType || inferLoopMediaTypeFromUrl(url),
  };

  if (!existing || next.order < existing.order || next.priority < existing.priority) {
    assetsByUrl.set(url, { ...existing, ...next });
  }
};

const collectStreamAssets = (assetsByUrl, stream, source, orderBase) => {
  const content = getStreamContent(stream);
  readMediaUrlsFromContent(content).forEach((url, index) => {
    addAsset(assetsByUrl, {
      url,
      role: source === 'background_stream' ? 'house_background' : 'house_default_stream',
      source,
      priority: LOOP_MEDIA_PRIORITY.houseDefault,
      order: orderBase + index,
    });
  });
};

const isSelfPromoAd = (ad) => Boolean(
  ad?.self_promo
  || ad?.content?.selfPromo
  || ad?.content?.self_promo
  || ad?.creative?.self_promo
);

const collectAdvertisementAssets = (assetsByUrl, ad, adIndex) => {
  const priority = isSelfPromoAd(ad)
    ? LOOP_MEDIA_PRIORITY.houseDefault
    : LOOP_MEDIA_PRIORITY.paidAd;
  const role = isSelfPromoAd(ad) ? 'self_promo_ad' : 'paid_ad';
  const baseOrder = 3000 + adIndex * 100;

  readMediaUrlsFromContent(ad?.content).forEach((url, index) => {
    addAsset(assetsByUrl, {
      url,
      role,
      source: 'overlay_advertisements.content',
      priority,
      order: baseOrder + index,
    });
  });

  readMediaUrlsFromContent(ad?.creative).forEach((url, index) => {
    addAsset(assetsByUrl, {
      url,
      role,
      source: 'overlay_advertisements.creative',
      priority,
      order: baseOrder + 25 + index,
    });
  });

  asArray(ad?.creative?.assets).forEach((asset, index) => {
    addAsset(assetsByUrl, {
      url: asset?.url,
      role,
      source: `overlay_advertisements.creative.assets.${asset?.type || 'asset'}`,
      priority,
      order: baseOrder + 50 + index,
    });
  });
};

const collectCreativeRotationAssets = (assetsByUrl, content) => {
  const segments = asArray(content?.playback_policy?.loop_plan?.segments);
  segments.forEach((segment, segmentIndex) => {
    const tier = String(segment?.tier || '').trim().toLowerCase();
    const candidates = asArray(segment?.creative_rotation?.candidates);
    candidates.forEach((candidate, candidateIndex) => {
      const source = String(candidate?.creative_source || '').trim().toLowerCase();
      const isHouse = tier === 'house' || source === 'default_stream';
      addAsset(assetsByUrl, {
        url: candidate?.asset_url || candidate?.assetUrl,
        role: isHouse ? 'house_rotation_candidate' : 'paid_rotation_candidate',
        source: 'playback_policy.loop_plan.creative_rotation',
        priority: isHouse ? LOOP_MEDIA_PRIORITY.houseDefault : LOOP_MEDIA_PRIORITY.paidAd,
        order: 2000 + segmentIndex * 100 + candidateIndex,
      });
    });
  });
};

const isHouseResolvedCreative = (segment, creative) => {
  const tier = String(segment?.tier || '').trim().toLowerCase();
  const source = String(creative?.source || creative?.creative_source || '').trim().toLowerCase();
  return tier === 'house'
    || source === 'self_promo'
    || source === 'house'
    || source === 'default_stream';
};

const collectResolvedLoopPlanAssets = (assetsByUrl, content) => {
  const resolvedLoopPlan = content?.resolved_loop_plan_v2?.loop_plan || {};
  const segments = asArray(resolvedLoopPlan?.segments);
  segments.forEach((segment, segmentIndex) => {
    const creative = segment?.resolved_creative || segment?.resolvedCreative || null;
    if (!creative) return;

    const isHouse = isHouseResolvedCreative(segment, creative);
    [
      creative.url,
      creative.asset_url,
      creative.assetUrl,
      creative.video,
      creative.video_url,
      creative.image_url,
      creative.media?.url,
      creative.asset?.url,
    ].forEach((url, urlIndex) => {
      addAsset(assetsByUrl, {
        url,
        role: isHouse ? 'resolved_self_promo_creative' : 'resolved_paid_creative',
        source: 'resolved_loop_plan_v2.loop_plan.resolved_creative',
        priority: isHouse ? LOOP_MEDIA_PRIORITY.houseDefault : LOOP_MEDIA_PRIORITY.paidAd,
        media_type: creative.media_type || creative.mediaType,
        order: 1500 + segmentIndex * 100 + urlIndex,
      });
    });
  });
};

export const extractLoopMediaAssets = (content) => {
  const assetsByUrl = new Map();

  collectStreamAssets(assetsByUrl, content?.background_stream, 'background_stream', 0);
  asArray(content?.all_default_streams).forEach((stream, index) => {
    collectStreamAssets(assetsByUrl, stream, 'all_default_streams', 1000 + index * 100);
  });

  asArray(content?.overlay_advertisements).forEach((ad, index) => {
    collectAdvertisementAssets(assetsByUrl, ad, index);
  });
  collectResolvedLoopPlanAssets(assetsByUrl, content);
  collectCreativeRotationAssets(assetsByUrl, content);

  [
    content?.branding_overlay?.logo_transformed_url,
    content?.branding_overlay?.logo_url,
  ].forEach((url, index) => {
    addAsset(assetsByUrl, {
      url,
      role: 'branding_overlay',
      source: 'branding_overlay',
      priority: LOOP_MEDIA_PRIORITY.houseDefault,
      order: 2500 + index,
    });
  });

  return Array.from(assetsByUrl.values())
    .sort((a, b) => a.order - b.order || a.priority - b.priority || a.url.localeCompare(b.url))
    .map(({ order, ...asset }) => asset);
};

const writeInventory = (screenId, inventory) => {
  try {
    const storage = getStorage();
    if (!storage) return false;
    storage.setItem(buildLoopMediaInventoryKey(screenId), JSON.stringify(inventory));
    return true;
  } catch (_) {
    return false;
  }
};

const readInventory = (screenId) => {
  try {
    const storage = getStorage();
    const raw = storage?.getItem(buildLoopMediaInventoryKey(screenId));
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
};

const publishInventoryStatus = (screenId, inventory) => {
  try {
    if (typeof window === 'undefined') return;
    window.__TRILLBOARDS_LOOP_MEDIA_CACHE__ = {
      screen_id: String(screenId || ''),
      updated_at_ms: inventory.updated_at_ms,
      required_asset_count: inventory.required_asset_count,
      cached_asset_count: inventory.cached_asset_count,
      missing_asset_count: inventory.missing_asset_count,
      failed_asset_count: inventory.failed_asset_count || 0,
      skipped_asset_count: inventory.skipped_asset_count || 0,
    };
  } catch (_) {}
};

export const normalizeLoopMediaInventory = (inventory, options = {}) => {
  if (!inventory || typeof inventory !== 'object') {
    return { hit: false, reason: 'miss' };
  }

  const nowMs = Number.isFinite(Number(options.nowMs)) ? Number(options.nowMs) : Date.now();
  const maxAgeMs = Number.isFinite(Number(options.maxAgeMs))
    ? Number(options.maxAgeMs)
    : LOOP_MEDIA_INVENTORY_MAX_AGE_MS;
  const updatedAtMs = Number(inventory.updated_at_ms);
  const ageMs = Number.isFinite(updatedAtMs) ? Math.max(0, nowMs - updatedAtMs) : null;

  if (!Number.isFinite(updatedAtMs)) {
    return { hit: false, reason: 'invalid_timestamp' };
  }

  if (Number.isFinite(maxAgeMs) && ageMs > maxAgeMs) {
    return { hit: false, reason: 'stale', ageMs };
  }

  return {
    hit: true,
    ageMs,
    inventory,
    requiredAssetCount: Number(inventory.required_asset_count) || 0,
    cachedAssetCount: Number(inventory.cached_asset_count) || 0,
    missingAssetCount: Number(inventory.missing_asset_count) || 0,
  };
};

export const loadLoopMediaInventory = (screenId, options = {}) => {
  const inventory = readInventory(screenId);
  return normalizeLoopMediaInventory(inventory, options);
};

export const getLoopMediaCacheStatus = (screenId, content = null) => {
  const requiredAssetCount = extractLoopMediaAssets(content).length;
  const liveStatus = typeof window !== 'undefined'
    ? window.__TRILLBOARDS_LOOP_MEDIA_CACHE__
    : null;

  if (liveStatus?.screen_id === String(screenId || '')) {
    return {
      requiredAssetCount: Number(liveStatus.required_asset_count) || requiredAssetCount,
      cachedAssetCount: Number(liveStatus.cached_asset_count) || 0,
      missingAssetCount: Number(liveStatus.missing_asset_count) || 0,
    };
  }

  const inventory = loadLoopMediaInventory(screenId);
  if (inventory.hit) {
    return {
      requiredAssetCount: inventory.requiredAssetCount || requiredAssetCount,
      cachedAssetCount: inventory.cachedAssetCount,
      missingAssetCount: inventory.missingAssetCount,
    };
  }

  return {
    requiredAssetCount,
    cachedAssetCount: 0,
    missingAssetCount: requiredAssetCount,
  };
};

export const prefetchLoopMediaAssets = async (screenId, content, options = {}) => {
  const assets = extractLoopMediaAssets(content);
  const nowMs = Number.isFinite(Number(options.nowMs)) ? Number(options.nowMs) : Date.now();
  const results = [];

  for (const asset of assets) {
    try {
      const cachedUrl = await contentCache.cacheMediaBlob(asset.url, {
        priority: asset.priority,
        expectedSize: asset.expected_size || null,
      });
      const cached = typeof cachedUrl === 'string' && cachedUrl.startsWith('blob:');
      let nativeMedia = { saved: false, reason: 'not_cached' };
      if (cached && typeof contentCache.getCachedMediaRecord === 'function') {
        const mediaRecord = await contentCache.getCachedMediaRecord(asset.url);
        nativeMedia = await putNativeMediaAsset(screenId, options.fingerprint, asset, mediaRecord);
      }
      results.push({
        ...asset,
        cached,
        native_cached: nativeMedia.saved === true,
        native_cache_reason: nativeMedia.saved ? null : nativeMedia.reason,
      });
    } catch (error) {
      results.push({
        ...asset,
        cached: false,
        native_cached: false,
        error: error?.message || 'cache_failed',
      });
    }
  }

  const cachedAssetCount = results.filter((result) => result.cached).length;
  const nativeCachedAssetCount = results.filter((result) => result.native_cached).length;
  const failedAssetCount = results.filter((result) => !result.cached).length;
  const inventory = {
    version: 1,
    screen_id: String(screenId || ''),
    updated_at_ms: nowMs,
    updated_at: new Date(nowMs).toISOString(),
    required_asset_count: assets.length,
    cached_asset_count: cachedAssetCount,
    native_cached_asset_count: nativeCachedAssetCount,
    missing_asset_count: Math.max(0, assets.length - cachedAssetCount),
    failed_asset_count: failedAssetCount,
    skipped_asset_count: 0,
    assets: results.map((result) => ({
      url: result.url,
      role: result.role,
      source: result.source,
      priority: result.priority,
      ttl_ms: result.ttl_ms,
      cached: result.cached,
      native_cached: result.native_cached === true,
      native_cache_reason: result.native_cache_reason || null,
      error: result.error || null,
    })),
  };

  writeInventory(screenId, inventory);
  putNativeAssetInventory(screenId, options.fingerprint, inventory);
  publishInventoryStatus(screenId, inventory);

  return {
    screenId,
    requiredAssetCount: assets.length,
    cachedAssetCount,
    nativeCachedAssetCount,
    missingAssetCount: inventory.missing_asset_count,
    failedAssetCount,
    inventory,
  };
};

const replaceUrlsInClone = (value, replacements) => {
  if (!value || replacements.size === 0) return value;
  if (typeof value === 'string') {
    return replacements.get(value)?.cachedUrl || value;
  }
  if (Array.isArray(value)) {
    return value.map((item) => replaceUrlsInClone(item, replacements));
  }
  if (typeof value === 'object') {
    let mediaReplacement = null;
    const clone = Object.keys(value).reduce((acc, key) => {
      const original = value[key];
      const replacement = typeof original === 'string' ? replacements.get(original) : null;
      if (replacement && CACHE_METADATA_URL_KEYS.has(key) && !mediaReplacement) {
        mediaReplacement = replacement;
      }
      acc[key] = replaceUrlsInClone(original, replacements);
      return acc;
    }, {});
    if (mediaReplacement) {
      if (!clone._trillboards_original_asset_url) {
        clone._trillboards_original_asset_url = mediaReplacement.originalUrl;
      }
      if (!clone._trillboards_media_type) {
        clone._trillboards_media_type = mediaReplacement.mediaType;
      }
    }
    return clone;
  }
  return value;
};

export const hydrateLoopMediaFromCache = async (screenId, content, options = {}) => {
  const assets = extractLoopMediaAssets(content);
  const replacements = new Map();

  for (const asset of assets) {
    try {
      const cachedUrl = await contentCache.getCachedMediaUrl(asset.url);
      if (cachedUrl) {
        replacements.set(asset.url, {
          cachedUrl,
          originalUrl: asset.url,
          mediaType: asset.media_type || inferLoopMediaTypeFromUrl(asset.url),
        });
        if (typeof contentCache.getCachedMediaRecord === 'function') {
          const mediaRecord = await contentCache.getCachedMediaRecord(asset.url);
          await putNativeMediaAsset(screenId, options.fingerprint, asset, mediaRecord);
        }
      }
    } catch (_) {}
  }

  const cachedAssetCount = replacements.size;
  const nowMs = Number.isFinite(Number(options.nowMs)) ? Number(options.nowMs) : Date.now();
  const inventory = {
    version: 1,
    screen_id: String(screenId || ''),
    updated_at_ms: nowMs,
    updated_at: new Date(nowMs).toISOString(),
    required_asset_count: assets.length,
    cached_asset_count: cachedAssetCount,
    missing_asset_count: Math.max(0, assets.length - cachedAssetCount),
    failed_asset_count: 0,
    skipped_asset_count: 0,
    assets: assets.map((asset) => ({
      ...asset,
      cached: replacements.has(asset.url),
      error: null,
    })),
  };

  writeInventory(screenId, inventory);
  putNativeAssetInventory(screenId, options.fingerprint, inventory);
  publishInventoryStatus(screenId, inventory);

  return {
    content: replacements.size > 0 ? replaceUrlsInClone(content, replacements) : content,
    requiredAssetCount: assets.length,
    cachedAssetCount,
    missingAssetCount: inventory.missing_asset_count,
    inventory,
  };
};
